import { SyntheticEvent, useLayoutEffect, useState } from "react";
import { IonButton, IonHeader, IonInput, IonItem, IonLabel } from "@ionic/react";
import { translate } from "components/string-table";
import {
  Alert,
  App,
  AuthLayout,
  Colors,
  DefaultLoginErrorBanner,
  PasswordField,
  PasswordValidationField,
  useAuth,
  ValidationRegex,
} from "main";
import { RouteComponentProps } from "react-router";

import styles from "./ForgotPassword.module.css";

type ForgotPasswordProps = {
  app: App;
  history: RouteComponentProps["history"];
  location: RouteComponentProps["location"];
  loginWithPasswordPath: string;
};

export function ForgotPassword(props: ForgotPasswordProps) {
  const { app, history, location, loginWithPasswordPath } = props;

  /***** Constants *****/
  const locationState = location.state as { email: "value" } | undefined;

  // redirect if no email exists on location state (e.g when directly visiting /forgot-password)
  useLayoutEffect(() => {
    if (!locationState || !locationState.email) {
      return history.push("/");
    }
  }, [history, locationState]);

  /***** Hooks *****/
  const { forgotPasswordSubmit } = useAuth();
  const [form, setForm] = useState<ForgotPasswordFormState>({
    email: locationState && locationState.email ? locationState.email : "",
    newPassword: "",
    validationCode: "",
  });
  const [codeValidationError, setCodeValidationError] = useState<ResetPasswordErrorState>({
    hasError: false,
    errorType: "",
  });
  const [validationArray, setValidationArray] = useState<ValidationObjectType[]>(validationObject);

  /***** Handlers *****/
  function onPasswordChange(value: string) {
    setForm((prevForm) => ({ ...prevForm, newPassword: value }));
    setValidationArray(validatePassword(value, validationArray));
  }

  function onBackToLogin(isPasswordReset: boolean) {
    history.push({
      pathname: loginWithPasswordPath,
      state: {
        email: form.email,
        isPasswordReset: isPasswordReset,
      },
    });
  }

  async function onResetPassword(e: SyntheticEvent) {
    e.preventDefault();
    await forgotPasswordSubmit(form.email, form.validationCode, form.newPassword)
      .then(() => {
        setCodeValidationError({ hasError: false });
        onBackToLogin(true);
        console.info("Password reset.");
      })
      .catch((err: Error) => {
        setCodeValidationError({ hasError: true, errorType: err.name });
        console.error("UI - forgotPasswordSubmit error: ", err);
      });
  }

  /***** Render *****/
  return (
    <AuthLayout
      app={app}
      title={translate("FORGOT_PASSWORD_RESET_PASSWORD_HEADER_LABEL")}
      onBackNav={() => onBackToLogin(false)}
    >
      {codeValidationError.hasError ? (
        <ResetPasswordErrorMessage codeValidationError={codeValidationError} />
      ) : (
        <Alert color="info">
          {translate("FORGOT_PASSWORD_RESET_PASSWORD_BANNER_MESSAGE", {
            email: maskEmail(form.email) || "",
          })}
        </Alert>
      )}

      <form className={styles.form} onSubmit={async (e) => onResetPassword(e)}>
        <VerificationCodeField
          code={form.validationCode}
          onVerificationCodeChange={(code: string) => {
            setForm((prevForm) => ({ ...prevForm, validationCode: code }));
          }}
        />
        <PasswordField
          passwordLabel={translate("FORGOT_PASSWORD_RESET_PASSWORD_FIELD_LABEL")}
          onPasswordInput={(value: string) => onPasswordChange(value)}
          password={form.newPassword}
        />
        <PasswordValidationField validationObject={validationArray} />
        <IonButton
          data-testid="forget-password-submit-button"
          type="submit"
          size="default"
          expand="block"
          className={styles.resetPasswordButton}
          disabled={!isFormValid(form, validationArray)}
        >
          {translate("FORGOT_PASSWORD_RESET_PASSWORD_SUBMIT_LABEL")}
        </IonButton>
      </form>
    </AuthLayout>
  );
}

type ForgotPasswordFormState = {
  email: string;
  newPassword: string;
  validationCode: string;
};

type ResetPasswordErrorState = {
  hasError: boolean;
  errorType?: string;
};

function isFormValid(form: ForgotPasswordFormState, validationArray: ValidationObjectType[]) {
  const { validationCode } = form;
  return checkPasswordValidation(validationArray) && validationCode.length !== 0;
}

export const maskEmail = (email?: string) => {
  if (!email) return;

  const [name, domain] = email.split("@");
  const asterisks = "***";
  return `${name[0]}${asterisks}@${domain[0]}${asterisks}`;
};

export const validatePassword = (password: string, validationArray: ValidationObjectType[]) => {
  const tempValidationArray = [...validationArray];
  tempValidationArray.forEach((validation) => {
    const validationRegex = new RegExp(validation.validationType);
    const tempIsValid = validationRegex.test(password);
    validation.isValid = tempIsValid;
  });
  return tempValidationArray;
};

export const checkPasswordValidation = (validationArray: ValidationObjectType[]) => {
  return validationArray.every((item) => item.isValid);
};

export type ValidationObjectType = {
  validationType: (typeof ValidationRegex)[keyof typeof ValidationRegex];
  message: string;
  isValid: boolean;
};

const validationObject: ValidationObjectType[] = [
  {
    validationType: ValidationRegex.RESET_VALIDATION_UPPERCASE,
    message: translate("FORGOT_PASSWORD_RESET_VALIDATION_UPPERCASE"),
    isValid: false,
  },
  {
    validationType: ValidationRegex.RESET_VALIDATION_LOWERCASE,
    message: translate("FORGOT_PASSWORD_RESET_VALIDATION_LOWERCASE"),
    isValid: false,
  },
  {
    validationType: ValidationRegex.RESET_VALIDATION_NUMBER,
    message: translate("FORGOT_PASSWORD_RESET_VALIDATION_NUMBER"),
    isValid: false,
  },
  {
    validationType: ValidationRegex.RESET_VALIDATION_MIN_CHARACTERS,
    message: translate("FORGOT_PASSWORD_RESET_VALIDATION_MIN_CHARACTERS"),
    isValid: false,
  },
];

type ValidationCodeProps = {
  code: string;
  onVerificationCodeChange: (e: string) => void;
};

function VerificationCodeField(props: ValidationCodeProps) {
  const { code, onVerificationCodeChange } = props;
  return (
    <div style={{ width: "inherit" }}>
      <IonHeader style={{ paddingLeft: 16, boxShadow: "none" }}>
        <IonLabel style={{ fontWeight: "var(--swing-font-medium)" }}>
          {translate("FORGOT_PASSWORD_RESET_PASSWORD_VERIFICATION_LABEL")}
        </IonLabel>
      </IonHeader>
      <IonItem
        lines="full"
        className={`${styles.backgroundGray} ion-margin-bottom`}
        css={{
          width: "100%",
          "--border-color": Colors.slate400,
          "--min-height": "auto",
        }}
      >
        <IonInput
          data-testid="verification-code-input"
          type="text"
          value={code}
          onIonInput={(e: CustomEvent) => {
            onVerificationCodeChange(e.detail.value);
          }}
          spellCheck="false"
        />
      </IonItem>
    </div>
  );
}

type ResetPasswordErrorProps = {
  codeValidationError: ResetPasswordErrorState;
};

function ResetPasswordErrorMessage(props: ResetPasswordErrorProps) {
  const { codeValidationError } = props;
  switch (codeValidationError.errorType) {
    case "CodeMismatchException":
      return (
        <Alert showIcon title={translate("FORGOT_PASSWORD_LOGIN_ERROR_TITLE")} color="danger">
          {translate("FORGOT_PASSWORD_RESET_PASSWORD_INVALID_CODE_MESSAGE")}.
        </Alert>
      );
    case "ExpiredCodeException":
      return (
        <Alert
          showIcon
          title={translate("FORGOT_PASSWORD_RESET_VERIFICATION_CODE_EXPIRED_TITLE")}
          color="danger"
        >
          {translate("FORGOT_PASSWORD_RESET_VERIFICATION_CODE_EXPIRED_MESSAGE")}.
        </Alert>
      );
    default:
      return <DefaultLoginErrorBanner />;
  }
}
